import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {alpha, Theme} from "@mui/material/styles";
import {useIdTypeValues} from "crqlar-react-common/dist/components/CList/useIdTypeValues";
import GenericValueInput from "crqlar-react-common/dist/components/filters/input/GenericValueInput";
import FilterLanguageInput from "crqlar-react-common/dist/components/filters/input/FilterLanguageInput";
import {
    DataType,
    DataTypeOperator,
    FilterRule
} from "crqlar-react-common/dist/models/audience/filter.model";
import {FilterOption} from "crqlar-react-common/dist/models/audience/filterOption.model";
import {formatDateLocalISO} from "crqlar-react-common/dist/utils/date.util";
import {getFilterOptionLabel} from "crqlar-react-common/dist/utils/filter.util";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {SERVER_TABLE_PANEL_WIDTH} from "./constants";
import {
    changeFilterRuleOperator,
    FILTER_OPERATORS_WITHOUT_VALUE,
    FILTER_TWO_VALUE_OPERATORS,
    getFilterRuleValues,
    getIdTypeValueSource,
    getSupportedFilterOperators,
    hasIdTypeValueEditor,
    isCompleteFilterRule
} from "./filterRuleContract";

interface FilterPanelProps {
    filterOptions: FilterOption[]
    rules: FilterRule[]
    onChange: (rules: FilterRule[]) => void
    onClose?: () => void
}

const placeholderSx = {
    color: 'text.disabled',
    opacity: 1
} as const;

const inputPlaceholderSx = {
    '& .MuiInputBase-input::placeholder': placeholderSx
} as const;

const filterRuleLabelSx = {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
} as const;

const formatDateTimeLocalValue = (value: Date) => {
    const pad = (part: number) => String(part).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
        + `T${pad(value.getHours())}:${pad(value.getMinutes())}`;
};

const operatorTranslationKey = (operator: DataTypeOperator, type?: DataType) => {
    const isDate = type === DataType.LOCAL_DATE || type === DataType.TIMESTAMP;
    if (isDate) return `Data type operator ${operator} date`;
    // Nullable flags are only ever written as true, so "does not exist" is the real "no" —
    // reading it as True/Does not exists makes the negative side look like a bug.
    if (type === DataType.BOOL) return `Data type operator ${operator} bool`;
    return `Data type operator ${operator}`;
};

interface FilterRuleTextProps {
    rule: FilterRule
    option?: FilterOption
    filterOptions: FilterOption[]
    valueLabel: string
}

const FilterRuleText = ({rule, option, filterOptions, valueLabel}: FilterRuleTextProps) => {
    const {t, i18n} = useTranslation();
    const label = [
        getFilterOptionLabel(filterOptions, rule.field, i18n.language),
        rule.operator ? t(operatorTranslationKey(rule.operator, option?.dataType)) : '',
        valueLabel
    ].filter(Boolean).join(' ');

    return (
        <Typography variant="body1" sx={filterRuleLabelSx} title={label}>
            {label}
        </Typography>
    );
};

const LookupFilterRuleText = ({rule, option, filterOptions}: Omit<FilterRuleTextProps, 'valueLabel'>) => {
    const ids = (rule.textValues ?? []).filter((value): value is string => !!value);
    const resolvedValues = useIdTypeValues(option?.idType, ids);
    const valueLabel = ids.map(id => (
        resolvedValues.find(value => value.id === id)?.name || id
    )).join(', ');

    return (
        <FilterRuleText
            rule={rule}
            option={option}
            filterOptions={filterOptions}
            valueLabel={valueLabel}
        />
    );
};

const FilterRuleLabel = ({rule, filterOptions}: Pick<FilterRuleTextProps, 'rule' | 'filterOptions'>) => {
    const {t} = useTranslation();
    const option = filterOptions.find(filterOption => filterOption.field === rule.field);
    const idTypeSource = getIdTypeValueSource(option);

    if (hasIdTypeValueEditor(option) && !idTypeSource) {
        return <LookupFilterRuleText rule={rule} option={option} filterOptions={filterOptions}/>;
    }

    let valueLabel = '';
    if (rule.operator && !FILTER_OPERATORS_WITHOUT_VALUE.has(rule.operator)) {
        if (idTypeSource) {
            const values = (rule.textValues ?? [])
                .filter((value): value is string => !!value);
            valueLabel = (idTypeSource.displaySelection?.(values) ?? values)
                .map(idTypeSource.labelFor)
                .join(', ');
        } else if (option?.dataType === DataType.ENUM) {
            valueLabel = (rule.textValues ?? [])
                .filter((value): value is string => !!value)
                .map(value => option.predefinedValues?.find(item => item.cid === value)?.name ?? value)
                .join(', ');
        } else if (option?.dataType === DataType.BOOL) {
            const value = rule.textValues?.[0];
            valueLabel = value === 'true' ? t('Yes') : value === 'false' ? t('No') : '';
        } else {
            valueLabel = getFilterRuleValues(rule)
                .filter(value => value !== undefined && value !== '')
                .map(value => value instanceof Date ? value.toLocaleDateString() : String(value))
                .join(' – ');
        }
    }

    return (
        <FilterRuleText
            rule={rule}
            option={option}
            filterOptions={filterOptions}
            valueLabel={valueLabel}
        />
    );
};

const FilterPanel = ({
    filterOptions,
    rules,
    onChange,
    onClose
}: FilterPanelProps) => {
    const {t, i18n} = useTranslation();
    const [isFormOpen, setIsFormOpen] = useState(rules.length === 0);
    const [draft, setDraft] = useState<FilterRule>({});
    const [editingIndex, setEditingIndex] = useState<number | null>(null);

    const optionFor = (field?: string) => filterOptions.find(option => option.field === field);
    const usableFilterOptions = filterOptions.filter(option => getSupportedFilterOperators(option).length > 0);
    const draftOption = optionFor(draft.field);
    const dataType = draftOption?.dataType;
    const supportedOperators = draftOption ? getSupportedFilterOperators(draftOption) : [];
    const canEnterValue = !!draft.operator && !FILTER_OPERATORS_WITHOUT_VALUE.has(draft.operator);
    const valueIndexes = draft.operator && FILTER_TWO_VALUE_OPERATORS.has(draft.operator) ? [0, 1] : [0];

    const operatorLabel = (operator: DataTypeOperator, type?: DataType) => {
        return t(operatorTranslationKey(operator, type));
    };

    const startAdd = () => {
        setDraft({});
        setEditingIndex(null);
        setIsFormOpen(true);
    };

    const startEdit = (index: number) => {
        setDraft(rules[index]);
        setEditingIndex(index);
        setIsFormOpen(true);
    };

    const cancelForm = () => {
        if (rules.length) {
            setIsFormOpen(false);
        } else {
            onClose?.();
        }
    };

    const apply = () => {
        if (!isCompleteFilterRule(draft, draftOption)) return;

        const nextRules = [...rules];
        if (editingIndex === null) {
            nextRules.push(draft);
        } else {
            nextRules[editingIndex] = draft;
        }

        onChange(nextRules);
        onClose?.();
    };

    const rawValueAt = (index: number) => {
        switch (dataType) {
            case DataType.NUMBER:
                return draft.longValues?.[index]?.toString() ?? '';
            case DataType.DECIMAL:
                return draft.doubleValues?.[index]?.toString() ?? '';
            case DataType.LOCAL_DATE:
                return draft.localDateValues?.[index] ?? '';
            case DataType.TIMESTAMP: {
                const value = draft.timestampValues?.[index];
                return value ? formatDateTimeLocalValue(new Date(value)) : '';
            }
            default:
                return draft.textValues?.[index] ?? '';
        }
    };

    const setDraftValue = (raw: string, index: number) => {
        setDraft(previous => {
            const base: FilterRule = {field: previous.field, operator: previous.operator};
            const assign = <T, >(existing: (T | undefined)[] | undefined, value: T | undefined) => {
                const next = [...(existing ?? [])];
                next[index] = value;
                return next;
            };
            const parsed = raw === '' ? undefined : Number(raw);
            const safeNumber = parsed !== undefined && Number.isFinite(parsed) ? parsed : undefined;

            switch (dataType) {
                case DataType.NUMBER:
                    return {
                        ...base,
                        longValues: assign(
                            previous.longValues,
                            safeNumber === undefined ? undefined : Math.round(safeNumber)
                        )
                    };
                case DataType.DECIMAL:
                    return {...base, doubleValues: assign(previous.doubleValues, safeNumber)};
                case DataType.LOCAL_DATE:
                    return {...base, localDateValues: assign(previous.localDateValues, raw || undefined)};
                case DataType.TIMESTAMP:
                    return {
                        ...base,
                        timestampValues: assign(previous.timestampValues, raw ? new Date(raw) : undefined)
                    };
                default:
                    return {...base, textValues: assign(previous.textValues, raw || undefined)};
            }
        });
    };

    // A field arrives with the first operator the API lists for it, so the value can be entered
    // straight away; the API orders the natural one first (On for dates, In for lists, Yes for flags).
    const selectField = (option: FilterOption | null) => {
        setDraft(option ? {field: option.field, operator: getSupportedFilterOperators(option)[0]} : {});
    };

    // Fills the first empty date, so a range can run from today or up to it.
    const setToday = () => {
        const index = valueIndexes.find(valueIndex => !rawValueAt(valueIndex)) ?? 0;
        setDraftValue(formatDateLocalISO(new Date()) ?? '', index);
    };

    const renderValueInput = (index: number) => {
        if (draftOption?.idType === 'language_code_iso_639_1') {
            const selected = (draft.textValues ?? []).filter((value): value is string => !!value);

            return (
                <FilterLanguageInput
                    key={index}
                    ids={selected}
                    disabled={!canEnterValue}
                    setIds={ids => setDraft(previous => ({
                        field: previous.field,
                        operator: previous.operator,
                        textValues: ids
                    }))}
                />
            );
        }

        const idTypeSource = getIdTypeValueSource(draftOption);
        if (idTypeSource) {
            const selected = (draft.textValues ?? []).filter((value): value is string => !!value);

            return (
                <Autocomplete
                    key={index}
                    size="small"
                    fullWidth
                    multiple
                    disabled={!canEnterValue}
                    options={idTypeSource.codes()}
                    value={selected}
                    getOptionLabel={idTypeSource.labelFor}
                    isOptionEqualToValue={(left, right) => left.toLowerCase() === right.toLowerCase()}
                    renderInput={params => (
                        <TextField
                            {...params}
                            placeholder={selected.length ? undefined : t('Value')}
                            sx={inputPlaceholderSx}
                        />
                    )}
                    onChange={(_, values) => setDraft(previous => ({
                        field: previous.field,
                        operator: previous.operator,
                        textValues: idTypeSource.normalizeSelection
                            ? idTypeSource.normalizeSelection(selected, values)
                            : values
                    }))}
                />
            );
        }

        if (hasIdTypeValueEditor(draftOption)) {
            if (!canEnterValue) {
                return (
                    <TextField
                        key={index}
                        size="small"
                        fullWidth
                        disabled
                        placeholder={t('Value')}
                        sx={inputPlaceholderSx}
                    />
                );
            }

            return (
                <Box key={index} sx={{width: '100%'}}>
                    <GenericValueInput
                        option={draftOption}
                        filterRow={draft}
                        setFilterRow={setDraft}
                        dataType={dataType}
                    />
                </Box>
            );
        }

        if (dataType === DataType.ENUM) {
            const choices = draftOption?.predefinedValues?.length
                ? draftOption.predefinedValues.map(value => ({value: value.cid, label: value.name}))
                : (draftOption?.enumValues ?? []).map(value => ({value, label: value}));
            const isMulti = draft.operator === DataTypeOperator.IN
                || draft.operator === DataTypeOperator.NOT_IN;

            if (isMulti) {
                const selected = (draft.textValues ?? []).filter((value): value is string => !!value);
                return (
                    <Autocomplete
                        key={index}
                        size="small"
                        fullWidth
                        multiple
                        disableCloseOnSelect={false}
                        disabled={!canEnterValue}
                        options={choices.map(choice => choice.value)}
                        value={selected}
                        getOptionLabel={value => choices.find(choice => choice.value === value)?.label ?? value}
                        isOptionEqualToValue={(left, right) => left === right}
                        renderInput={params => (
                            <TextField
                                {...params}
                                placeholder={selected.length ? undefined : t('Value')}
                                sx={inputPlaceholderSx}
                            />
                        )}
                        onChange={(_, values) => setDraft(previous => ({
                            field: previous.field,
                            operator: previous.operator,
                            textValues: values
                        }))}
                    />
                );
            }

            return (
                <Select
                    key={index}
                    size="small"
                    fullWidth
                    displayEmpty
                    disabled={!canEnterValue}
                    value={draft.textValues?.[0] ?? ''}
                    renderValue={value => value
                        ? choices.find(choice => choice.value === value)?.label ?? String(value)
                        : <Box component="span" sx={placeholderSx}>{t('Value')}</Box>}
                    onChange={event => setDraft(previous => ({
                        field: previous.field,
                        operator: previous.operator,
                        textValues: [event.target.value]
                    }))}
                >
                    {choices.map(choice => (
                        <MenuItem key={choice.value} value={choice.value}>{choice.label}</MenuItem>
                    ))}
                </Select>
            );
        }

        if (dataType === DataType.BOOL) {
            return (
                <Select
                    key={index}
                    size="small"
                    fullWidth
                    displayEmpty
                    disabled={!canEnterValue}
                    value={draft.textValues?.[0] ?? ''}
                    renderValue={value => value
                        ? value === 'true' ? t('Yes') : t('No')
                        : <Box component="span" sx={placeholderSx}>{t('Value')}</Box>}
                    onChange={event => setDraft(previous => ({
                        field: previous.field,
                        operator: previous.operator,
                        textValues: [event.target.value]
                    }))}
                >
                    <MenuItem value="true">{t('Yes')}</MenuItem>
                    <MenuItem value="false">{t('No')}</MenuItem>
                </Select>
            );
        }

        const inputType = dataType === DataType.NUMBER || dataType === DataType.DECIMAL
            ? 'number'
            : dataType === DataType.LOCAL_DATE
                ? 'date'
                : dataType === DataType.TIMESTAMP
                    ? 'datetime-local'
                    : 'text';

        return (
            <TextField
                key={index}
                size="small"
                fullWidth
                type={inputType}
                disabled={!canEnterValue}
                placeholder={t('Value')}
                sx={inputPlaceholderSx}
                value={rawValueAt(index)}
                onChange={event => setDraftValue(event.target.value, index)}
            />
        );
    };

    const operatorTooltip = !draft.field ? t('Select what to filter by first') : '';
    const valueTooltip = !draft.field
        ? t('Select what to filter by first')
        : !draft.operator
            ? t('Select an operator first')
            : !canEnterValue
                ? t('This operator needs no value')
                : '';

    return (
        <Stack sx={{width: SERVER_TABLE_PANEL_WIDTH, minWidth: 0}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 2, pb: 1, flexShrink: 0}}>
                <Typography variant="body1" fontWeight={500}>
                    {t('Filter')}
                </Typography>

                {onClose && (
                    <IconButton size="small" aria-label={t('Close')} onClick={onClose} sx={{ml: 'auto'}}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                )}
            </Box>

            <Box sx={{px: 2, pb: 2}}>
                {!isFormOpen ? (
                    <Stack gap={1}>
                        <Stack gap={0.5}>
                            {rules.map((rule, index) => (
                                <Stack
                                    key={`${rule.field}-${index}`}
                                    direction="row"
                                    alignItems="center"
                                    gap={0.5}
                                    sx={{
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        border: (theme: Theme) => `1px solid ${alpha(theme.palette.text.primary, 0.12)}`
                                    }}
                                >
                                    <FilterRuleLabel rule={rule} filterOptions={filterOptions}/>
                                    <Tooltip title={t('Edit')}>
                                        <IconButton size="small" onClick={() => startEdit(index)}>
                                            <EditOutlinedIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('Remove')}>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => onChange(rules.filter((_, ruleIndex) => ruleIndex !== index))}
                                        >
                                            <ClearIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            ))}
                        </Stack>

                        <Button
                            type="button"
                            size="small"
                            color="inherit"
                            startIcon={<AddIcon/>}
                            disabled={!usableFilterOptions.length}
                            onClick={startAdd}
                            sx={{alignSelf: 'flex-start', justifyContent: 'flex-start', textTransform: 'none'}}
                        >
                            {t('Add filter')}
                        </Button>
                    </Stack>
                ) : (
                    <Stack gap={1}>
                        <Autocomplete
                            size="small"
                            options={usableFilterOptions}
                            value={draftOption ?? null}
                            onChange={(_, option) => selectField(option)}
                            getOptionLabel={option => getFilterOptionLabel(filterOptions, option.field, i18n.language)}
                            isOptionEqualToValue={(left, right) => left.field === right.field}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    autoFocus
                                    placeholder={t('Filter by')}
                                    sx={inputPlaceholderSx}
                                />
                            )}
                        />

                        <Tooltip title={operatorTooltip}>
                            <Box>
                                <Select
                                    size="small"
                                    fullWidth
                                    displayEmpty
                                    disabled={!draft.field}
                                    value={draft.operator ?? ''}
                                    renderValue={value => value
                                        ? operatorLabel(value as DataTypeOperator, dataType)
                                        : <Box component="span" sx={placeholderSx}>{t('Operator')}</Box>}
                                    onChange={event => {
                                        const operator = event.target.value as DataTypeOperator;
                                        setDraft(previous => {
                                            return changeFilterRuleOperator(previous, operator);
                                        });
                                    }}
                                >
                                    {supportedOperators.map(operator => (
                                        <MenuItem key={operator} value={operator}>
                                            {operatorLabel(operator, dataType)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Tooltip>

                        {(!draft.operator || canEnterValue) && (
                            <Tooltip title={valueTooltip}>
                                <Stack direction="row" gap={1}>
                                    {valueIndexes.map(renderValueInput)}
                                </Stack>
                            </Tooltip>
                        )}

                        {dataType === DataType.LOCAL_DATE && canEnterValue && (
                            <Button
                                type="button"
                                size="small"
                                color="inherit"
                                startIcon={<TodayOutlinedIcon/>}
                                onClick={setToday}
                                sx={{alignSelf: 'flex-start', textTransform: 'none'}}
                            >
                                {t('Today')}
                            </Button>
                        )}

                        <Stack direction="row" justifyContent="flex-end" gap={1}>
                            <Button size="small" onClick={cancelForm} sx={{textTransform: 'none'}}>
                                {t('Cancel')}
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                disabled={!isCompleteFilterRule(draft, draftOption)}
                                onClick={apply}
                                sx={{textTransform: 'none'}}
                            >
                                {editingIndex === null ? t('Add filter') : t('Update filter')}
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </Box>
        </Stack>
    );
};

export default FilterPanel;
